import axios from 'axios'
import router from '@/router'
import * as Store from '@/vuex/store'
import Vue from 'vue'
import { logger } from '@/plugins/logger'
import { getTrackingHeaders } from '@/plugins/tracker'
import { fillPresetNames } from '@/components/reports/config/modules/common/timeRangeUtils'

function logError (event, properties = {}) {
  Vue.prototype.$tracker.capture(event, properties)
}

function handleError (e, showErrorMessage = false, returnErrorData = false) {
  logError('API_ERROR', e)
  if (e.response) {
    logger.error('api-error', {
      status: e.response.status,
      url: e.config?.url,
      method: e.config?.method,
      hasToken: !!e.config?.headers?.authorization,
    })
  }
  if (e.response && e.response.status === 401) {
    Store.store.dispatch('notifications/push', {
      icon: 'info',
      class: 'neutral',
      label: 'Session Expired',
    })
    router.push({
      name: 'login',
      query: { redirect: router.currentRoute.fullPath, },
    })
  }

  if (showErrorMessage) {
    Vue.prototype.$buefy.toast.open({
      message:
        e.response && e.response.data.detail
          ? `Error: ${e.response.data.detail}`
          : 'Error',
      type: 'is-warning',
    })
  }

  if (returnErrorData) {
    let data = e.response?.data
    if (data instanceof ArrayBuffer) {
      try {
        data = JSON.parse(new TextDecoder().decode(data))
      } catch (_) {
        return false
      }
    }
    return data?.detail || false
  }
  return false
}

const createAppApi = () => {
  const companyId = Store.store.getters['companyId']
  const authToken = Store.store.getters['auth/authToken']
  return axios.create({
    baseURL: `${process.env.VUE_APP_API_URL}/company/${companyId}/`,
    headers: { authorization: authToken, ...getTrackingHeaders(), },
  })
}

const createAdminApi = () => {
  const companyId = Store.store.getters['companyId']
  const authToken = Store.store.getters['auth/authToken']
  return axios.create({
    baseURL: `${process.env.VUE_APP_ADMIN_API_URL}/company/${companyId}/`,
    headers: { authorization: authToken, ...getTrackingHeaders(), },
  })
}

const createInternalApi = () => {
  const companyId = Store.store.getters['companyId']
  const authToken = Store.store.getters['auth/authToken']
  return axios.create({
    baseURL: `${process.env.VUE_APP_INTERNAL_API_URL}/company/${companyId}`,
    headers: { authorization: 'Bearer ' + authToken, ...getTrackingHeaders(), },
  })
}

const createExporterApi = () => {
  const companyId = Store.store.getters['companyId']
  const authToken = Store.store.getters['auth/authToken']
  return axios.create({
    baseURL: `${process.env.VUE_APP_EXPORTER_API_URL}/company/${companyId}`,
    headers: { authorization: 'Bearer ' + authToken, ...getTrackingHeaders(), },
  })
}

function companyObjectCrud (apiFactory, basePath) {
  return {
    async list () {
      try {
        const response = await apiFactory().get(`/${basePath}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async get (key) {
      try {
        const response = await apiFactory().get(`/${basePath}/${key}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async set (object) {
      try {
        const response = await apiFactory().post(`/${basePath}`, object)
        return {
          success: true,
          data: response.data,
        }
      } catch (e) {
        return {
          success: false,
          errors: handleError(e, false, true),
        }
      }
    },
  }
}

function companyObjectCrud2 (apiFactory, basePath) {
  return {
    async list () {
      try {
        const response = await apiFactory().get(`/${basePath}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async versions (key) {
      try {
        const response = await apiFactory().get(`/${basePath}/${key}/versions`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async get (key) {
      try {
        const response = await apiFactory().get(`/${basePath}/${key}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async set (object) {
      try {
        const response = await apiFactory().post(`/${basePath}`, object)
        return {
          success: true,
          data: response.data,
        }
      } catch (e) {
        return {
          success: false,
          errors: handleError(e, false, true),
        }
      }
    },
  }
}

export const apis = {
  app: {
    async getAllLocations () {
      try {
        const response = await createAppApi().get('/locations')
        return response.data
      } catch (e) {
        return handleError(e, true)
      }
    },
    async getReportingViews () {
      try {
        const response = await createAppApi().get('/views')
        return response.data
      } catch (e) {
        return handleError(e, true)
      }
    },
    async getExporterApiSpec () {
      try {
        const response = await axios.get(`${process.env.VUE_APP_EXPORTER_API_URL}/openapi.json`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },

  companyConfigs: {
    companyMeasure: companyObjectCrud(createExporterApi, 'definitions/company_measure'),
    companyDimension: companyObjectCrud(createExporterApi, 'definitions/company_dimension'),
    companyFilter: companyObjectCrud(createExporterApi, 'definitions/company_filter'),
    companyExpression: companyObjectCrud(createExporterApi, 'definitions/company_expression'),
    content: {
      measureGroups: companyObjectCrud2(createExporterApi, 'content/measure_groups'),
      dimensionGroups: companyObjectCrud2(createExporterApi, 'content/dimension_groups'),

      // Read-only: measure mappings are seeded rather than authored here.
      // `list` is the only verb the route offers.
      measureMappings: {
        async list () {
          try {
            const response = await createExporterApi().get('/content/measure_mappings')
            return response.data
          } catch (e) {
            return handleError(e)
          }
        },

        // Which of `measures` survive `key`, answered against the definition
        // graph. Not something the client can work out: a measure the mapping
        // does not name still resolves if the mapping applies inside its own
        // definition.
        async coverage (key, measures) {
          try {
            const response = await createExporterApi().post(
              `/content/measure_mappings/${key}/coverage`, { measures, }
            )
            return response.data
          } catch (e) {
            return handleError(e, false, true)
          }
        },
      },
    },

    async listCompanyScopes () {
      try {
        const response = await createExporterApi().get(`/company_scopes`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async listCompanyScopeOptions (companyScope) {
      try {
        const response = await createExporterApi().get(`/company_scopes/${companyScope.key}/options`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async listDashboardReports () {
      try {
        const response = await createExporterApi().get(`/dashboard_reports`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async validateDashboardReportConfig (reportConfig) {
      try {
        const response = await createExporterApi().post(
          `/dashboard_reports/validate`,
          reportConfig
        )
        return response.data
      } catch (e) {
        return handleError(e, false, true)
      }
    },

    async getDashboardReportDetails (dashboardKey) {
      try {
        const response = await createExporterApi().get(`/dashboard_reports/${dashboardKey}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getDashboardReportConfig (dashboardKey) {
      try {
        const response = await createExporterApi().get(`/dashboard_reports/${dashboardKey}/config`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async updateOptionalFilterSettings (dashboardKey, settings) {
      try {
        const response = await createExporterApi().put(
          `/dashboard_reports/${dashboardKey}/optional_filter_settings`,
          settings
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getDashboardFilterOptions (dashboardKey, filterKey, dashboardDataContext) {
      try {
        const response = await createExporterApi().post(
          `/dashboard_reports/${dashboardKey}/dashboard_filters/${filterKey}/filter_options`,
          dashboardDataContext
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getDashboardFilterOptionsWithMetadata (dashboardKey, filterKey, dashboardDataContext) {
      try {
        const response = await createExporterApi().post(
          `/dashboard_reports/${dashboardKey}/dashboard_filters/${filterKey}/filter_options_with_metadata`,
          dashboardDataContext
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getDashboardControlOptions (dashboardKey, controlKey) {
      try {
        const response = await createExporterApi().get(
          `/dashboard_reports/${dashboardKey}/dashboard_controls/${controlKey}/control_options`
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getDashboardModuleData (dashboardKey, moduleKey, dashboardDataContext, cancelToken) {
      try {
        const response = await createExporterApi().post(
          `/dashboard_reports/${dashboardKey}/dashboard_modules/${moduleKey}/load_data`,
           dashboardDataContext,
           cancelToken
        )
        return response.data
      } catch (e) {
         handleError(e)
         return { error: true, ...e, }
      }
    },

    async streamDashboardModuleData (dashboardKey, moduleKey, dashboardDataContext, callbacks, abortSignal) {
      return this._streamSSE(
        `/snap/dashboards/reports/${dashboardKey}/stream/modules/${moduleKey}`,
        dashboardDataContext,
        callbacks,
        abortSignal
      )
    },

    async streamDashboardFilterOptions (dashboardKey, filterKey, dashboardDataContext, callbacks, abortSignal) {
      return this._streamSSE(
        `/snap/dashboards/reports/${dashboardKey}/stream/filters/${filterKey}`,
        dashboardDataContext,
        callbacks,
        abortSignal
      )
    },

    async _streamSSE (path, body, callbacks, abortSignal) {
      const companyId = Store.store.getters['companyId']
      const authToken = Store.store.getters['auth/authToken']
      const url = `${process.env.VUE_APP_EXPORTER_API_URL}/company/${companyId}${path}`
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify(body),
          signal: abortSignal,
        })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let done = false
        while (!done) {
          const result = await reader.read()
          done = result.done
          if (done) break
          buffer += decoder.decode(result.value, { stream: true, })
          const parts = buffer.split('\n\n')
          buffer = parts.pop()
          for (const part of parts) {
            const eventMatch = part.match(/^event: (.+)$/m)
            const dataMatch = part.match(/^data: (.+)$/m)
            if (eventMatch && dataMatch) {
              const eventType = eventMatch[1]
              const data = JSON.parse(dataMatch[1])
              if (callbacks[eventType]) {
                callbacks[eventType](data)
              }
            }
          }
        }
      } catch (e) {
        if (e.name === 'AbortError') return
        handleError(e)
        if (callbacks.error) {
          callbacks.error({ message: e.message, })
        }
      }
    },

    async getDashboardModuleCsv (dashboardKey, moduleKey, dashboardDataContext) {
      try {
        const response = await createExporterApi().post(
          `/dashboard_reports/${dashboardKey}/dashboard_modules/${moduleKey}/load_csv_data`,
           dashboardDataContext
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async listSharedModules () {
      try {
        const response = await createExporterApi().get('/shared_modules')
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async shareModule (dashboardReportKey, dashboardModuleKey) {
      try {
        const response = await createExporterApi().post('/shared_modules', {
          dashboard_report_key: dashboardReportKey,
          dashboard_module_key: dashboardModuleKey,
        })
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async unshareModule (sharedModuleId) {
      try {
        await createExporterApi().delete(`/shared_modules/${sharedModuleId}`)
        return { success: true, }
      } catch (e) {
        return handleError(e)
      }
    },

    async analyzeConfig () {
      try {
        const response = await createExporterApi().get(`/definitions/analyze_config`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async previewMeasureValue (measure) {
      try {
        const response = await createExporterApi().post(`/definitions/measure/preview_value`, measure)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getMeasureDimensions (measure) {
      try {
        const response = await createExporterApi().post(`/definitions/measure/relevant_dimensions`, measure)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getMeasureJoinTree (measure) {
      try {
        const response = await createExporterApi().post(`/definitions/measure/joinable_tree`, measure)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getDimensionValues (dimension) {
      try {
        const response = await createExporterApi().post(`/definitions/dimension/values`, dimension)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getCompanyMeasures () {
      try {
        const response = await createExporterApi().get(`/definitions/company_measure`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getCompanyDimensions () {
      try {
        const response = await createExporterApi().get(`/definitions/company_dimension`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getCompanyExpressions () {
      try {
        const response = await createExporterApi().get(`/definitions/company_expression`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getCompanyExpression (key) {
      try {
        const response = await createExporterApi().get(`/definitions/company_expression/${key}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getBulkDefinitionTags (definitionType) {
      try {
        const response = await createExporterApi().get(`/definitions/bulk/${definitionType}/tags`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getCategoryTags (tagCategory) {
      try {
        const response = await createExporterApi().get(`/definitions/${tagCategory}/tags`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getBulkDefinitionUsages (definitionType) {
      const definitionTypeToUsageType = {
        company_measure: 'COMPANY_MEASURE',
        company_dimension: 'COMPANY_DIMENSION',
        company_filter: 'COMPANY_FILTER',
        company_expression: 'COMPANY_EXPRESSION',
      }
      const usageType = definitionTypeToUsageType[definitionType] || definitionType
      try {
        const response = await createExporterApi().get(`/usage/${usageType}/bulk`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
  dashboardStates: {
    async listStates (dashboardKey) {
      try {
        const response = await createExporterApi().get(`/dashboard_reports/${dashboardKey}/saved_states`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async createState (dashboardKey, stateBody) {
      try {
        const response = await createExporterApi().post(`/dashboard_reports/${dashboardKey}/saved_states`, stateBody)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async updateState (dashboardKey, stateId, stateBody) {
      try {
        const response = await createExporterApi().put(`/dashboard_reports/${dashboardKey}/saved_states/${stateId}`, stateBody)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async deleteState (dashboardKey, stateId) {
      try {
        const response = await createExporterApi().delete(`/dashboard_reports/${dashboardKey}/saved_states/${stateId}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
  reports: {
    async getReport (id) {
      try {
        const response = await createAdminApi().get(`/dashboard/${id}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async createNewReport (data) {
      try {
        const response = await createAdminApi().post(`/dashboard`, data)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
  integrations: {
    async getIntergatrionTypes () {
      try {
        const response = await createAdminApi().get('integration_types')
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async getUserIntegations () {
      try {
        const response = await createAdminApi().get('integrations')
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async addIntegration (body) {
      try {
        const response = await createAdminApi().post('integrations', body)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async removeIntegation (id) {
      try {
        const response = await createAdminApi().delete(`integrations/${id}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    // New stuff from exporters API
    async listIntegrationAccounts () {
      try {
        const response = await createExporterApi().get(`/integrations/accounts`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async doSomething (account) {
      try {
        const response = await createExporterApi().post(
          `integrations/accounts/do_something`,
          account
        )
        return response.data
      } catch (e) {
        return handleError(e, false, true)
      }
    },
  },
  company: {
    async getCompanyInfo () {
      try {
        const response = await createInternalApi().get(
          'entity_management/company'
        )
        return response.data
      } catch (e) {
        return handleError(e, true)
      }
    },
    async getAllLocations () {
      try {
        const response = await createInternalApi().get(
          'entity_management/locations'
        )
        return response.data
      } catch (e) {
        return handleError(e, true)
      }
    },
    async getLocation (id) {
      try {
        const response = await createInternalApi().get(
          `entity_management/location/${id}`
        )
        return response.data
      } catch (e) {
        return handleError(e, true)
      }
    },
  },
  modules: {
    async getAllModules () {
      try {
        const response = await createInternalApi().get('modules')
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async createModule (module) {
      try {
        const response = await createInternalApi().post('modules', module)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async getModule (id) {
      try {
        const response = await createInternalApi().get(`/modules/${id}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async deleteModule (id) {
      try {
        const response = await createInternalApi().delete(`/modules/${id}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async getModuleConfig (id) {
      try {
        const response = await createInternalApi().get(`/modules/${id}/config`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async updateModuleConfig (id, data) {
      try {
        const response = await createInternalApi().post(
          `modules/${id}/config`,
          { fields: data, }
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async getModuleContent (id, data) {
      try {
        const response = await createInternalApi().post(
          `modules/${id}/content`,
          data
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
  reporting: {
    async getDimensionValues (request) {
      try {
        const response = await createInternalApi().post(
          'reporting/dimension_values',
          request
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
  emailReports: {
    async getAllEmailReports () {
      try {
        const response = await createExporterApi().get(
          'email_management/email_reports'
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async createEmailReport (data) {
      try {
        const response = await createExporterApi().post(
          `email_management/email_reports`,
          data
        )
        return response.data
      } catch (e) {
        return handleError(e, true)
      }
    },
    async updateEmailReport (id, data) {
      try {
        const response = await createExporterApi().put(
          `email_management/email_reports/${id}`,
          data
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getEmailReportData (id) {
      try {
        const response = await createExporterApi().get(
          `email_management/email_reports/${id}`
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async deleteEmailReport (id) {
      try {
        const response = await createExporterApi().delete(
          `email_management/email_reports/${id}`
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getEmailHistory (
      statusSelection,
      sourceSelection
    ) {
      try {
        const response = await createExporterApi().get(
          'email_management/email_send_events',
          {
            params: {
              status: statusSelection.map(s => s.id),
              source: sourceSelection.map(s => s.id),
            },
          }
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async rerunAttempt (reportId, eventId) {
      try {
        const response = await createExporterApi().get(
          `email_management/email_reports/${reportId}/send_events/${eventId}/rerun`
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getReports () {
      try {
        const response = await createExporterApi().get(
          'email_management/report_pdfs'
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getReport (id) {
      try {
        const response = await createExporterApi().get(
          `email_management/report_pdfs/${id}`
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async saveReport (id, data) {
      try {
        const response = await createExporterApi().post(
          `email_management/report_pdfs/${id}`,
          data
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async sendEmailReport (id, data) {
      try {
        const response = await createExporterApi().post(
          `email_management/email_reports/${id}/send_email`,
          data
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async sendEmailReportAsync (id, data) {
      try {
        const response = await createExporterApi().post(
          `email_management/email_reports/${id}/send_email_async`,
          data
        )
        const task_id = response.data
        var status = 'PENDING'
        while (status == 'PENDING') {
          await new Promise(r => setTimeout(r, 1000))
          status = (await createExporterApi().get(
            `email_management/email_reports/${id}/attempt/${task_id}`
          )).data
        }
        return status == 'SUCCESS'
      } catch (e) {
        return handleError(e)
      }
    },
  },
  pdfReports: {
    async getAllPdfReports () {
      try {
        const response = await createExporterApi().get('pdf_reports')
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    // Returns { reports, email_usage, email_details_visible,
    // archived_report_keys }. Separate from getAllPdfReports so callers that
    // only want the config are unaffected.
    //
    // includeArchived widens the list rather than swapping it: the live
    // reports are always there, and archived ones join them. Which is which
    // comes back in archived_report_keys.
    async getAllPdfReportsWithUsage ({ includeArchived = false, } = {}) {
      try {
        const response = await createExporterApi().get(
          'pdf_reports/with_usage',
          { params: { include_archived: includeArchived, }, }
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    // Resolves to { report_key, is_archived } on success, or
    // { conflict: { code, message, references } } when an active email still
    // carries the report. The conflict is an answer, not a failure: the
    // caller shows the emails and offers to archive anyway.
    async archivePdfReport (id, { force = false, } = {}) {
      try {
        const response = await createExporterApi().post(
          `/pdf_report/${id}/archive`,
          null,
          { params: { force, }, }
        )
        return response.data
      } catch (e) {
        const body = e.response?.data
        if (e.response?.status === 409 && body?.code) {
          return { conflict: body, }
        }
        return handleError(e, true)
      }
    },

    async unarchivePdfReport (id) {
      try {
        const response = await createExporterApi().post(
          `/pdf_report/${id}/unarchive`
        )
        return response.data
      } catch (e) {
        return handleError(e, true)
      }
    },

    async createPdfReport (data) {
      try {
        const response = await createExporterApi().post(`/pdf_report`, data)
        return response.data
      } catch (e) {
        const detail = handleError(e, false, true)
        return detail ? { errors: detail, } : false
      }
    },

    async duplicatePdfReport (id, data) {
      try {
        const response = await createExporterApi().post(`/pdf_report/${id}/copy`, data)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async updatePdfReport (id, data) {
      try {
        const response = await createExporterApi().post(`/pdf_report/${id}`, data)
        return response.data
      } catch (e) {
        const detail = handleError(e, false, true)
        return detail ? { errors: detail, } : false
      }
    },

    async getPdfReportData (id) {
      try {
        const response = await createExporterApi().get(`/pdf_report/${id}`)
        return fillPresetNames(response.data)
      } catch (e) {
        return handleError(e)
      }
    },

    // Returns { report, email_usage, email_details_visible }.
    async getPdfReportWithUsage (id) {
      try {
        const response = await createExporterApi().get(`/pdf_report/${id}/with_usage`)
        return fillPresetNames(response.data)
      } catch (e) {
        return handleError(e)
      }
    },
    async createPdfReportModule (data) {
      try {
        const response = await createExporterApi().post(`/report_module`, data)
        return  response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async duplicatePdfReportModule (moduleId, data) {
      try {
        const response = await createExporterApi().post(`/report_module/${moduleId}/copy`, data)
        return fillPresetNames(response.data)
      } catch (e) {
        return handleError(e)
      }
    },

    async getPdfModuleData (moduleId) {
      try {
        const response = await createExporterApi().get(`/report_module/${moduleId}`)
        return fillPresetNames(response.data)
      } catch (e) {
        return handleError(e)
      }
    },

    async deletePdfReportModule (moduleId) {
      try {
        const response = await createExporterApi().delete(`/report_module/${moduleId}`)
        return  response.data
      } catch (e) {
        return handleError(e)
      }
    },
    async updatePdfReportModule (data) {
      try {
        const response = await createExporterApi().put(`/report_module/${data.id}`, data)
        return response.data
      } catch (e) {
        const detail = handleError(e, false, true)
        return detail ? detail : false
      }
    },
    async getPdfReportModulePreview (data) {
      try {
        const response = await createExporterApi().post(
          `pdf_reports/preview_module`,
          data
        )
        return response.data
      } catch (e) {
        const detail = handleError(e, false, true)
        return detail ? detail : false
      }
    },
    async getPdfReportEmailBodyPreview (data) {
      try {
        const response = await createExporterApi().post(
          `pdf_reports/preview_report_email_body`,
          data
        )
        return response.data
      } catch (e) {
        const detail = handleError(e, false, true)
        return detail ? detail : false
      }
    },
    async getPdfReportModuleEmailBodyPreview (data) {
      try {
        const response = await createExporterApi().post(
          `pdf_reports/preview_module_email_body`,
          data
        )
        return response.data
      } catch (e) {
        const detail = handleError(e, false, true)
        return detail ? detail : false
      }
    },
    async getPdfReportModuleDebug (data) {
      try {
        const response = await createExporterApi().post(
          `pdf_reports/debug_module`,
          data
        )
        return response.data
      } catch (e) {
        const detail = handleError(e, false, true)
        return detail ? detail : false
      }
    },

    async getPdfReportFullPreview (data) {
      try {
        const response = await createExporterApi().post(
          `pdf_reports/preview_report`, data
        )
        return response.data
      } catch (e) {
        return handleError(e, false, true)
      }
    },

    // --- AI report summaries ---
    // One route left. The rest went with the per-report summary panel; this
    // one serves the summarizer module editor, which is reachable without
    // AI_REPORT_SUMMARIES — the flag governs adding a summarizer, not
    // editing one that already exists.

    async getAiSummaryGuides () {
      try {
        const response = await createExporterApi().get(
          `ai_summaries/guides`
        )
        return response.data
      } catch (e) {
        return handleError(e, false, true)
      }
    },

    async getReportContent (id, data) {
      try {
        const response = await createInternalApi().post(
          `pdf_reports/${id}/content`,
          data
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getReportFilters (id) {
      try {
        const response = await createExporterApi().get(`/definitions/dimension/${id}/values`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
  expressions: {
    updateExpression () {},

    async getExpressionUsage (id) {
      try {
        const response = await createExporterApi().get(`/usage/expressions/${id}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getAttributeUsage (id) {
      try {
        const response = await createExporterApi().get(`/usage/attributes/${id}`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getUsagePaths (sourceId, sourceType, targetId, targetType) {
      try {
        const response = await createExporterApi().get('/usage/paths', {
          params: {
            source_id: sourceId,
            source_type: sourceType,
            target_id: targetId,
            target_type: targetType,
          },
        })
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getBulkExpressionFieldUsage () {
      try {
        const response = await createExporterApi().get(`/usage/fields/bulk`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getExpressionPreview (data) {
      try {
        const response = await createAppApi().post('/report', data)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },

    async getGenerationDiffs (generatorVersion) {
      try {
        const response = await createAdminApi().get(`expressions/generator/${generatorVersion}/diff`)
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
  forecastUploader: {
    async exportCsv (uploadType, requestBody) {
      try {
        const response = await createAppApi().post(
          `/budgets/upload_types/${uploadType}/export_csv`,
           requestBody
        )
        return response.data
      } catch (e) {
        return handleError(e)
      }
    },
  },
}

export default {
  install (Vue) {
    Vue.prototype.$apis = apis
  },
}
